'use client';
import React, { FC, useState } from 'react';
import { Button, LuvImage } from '@/components/ui';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import { TypeProduct } from '@/core/generated-types';
import classNames from 'classnames';
import {
  InventoryStatusCode,
  ShopifyProduct,
  ShopifyProductType,
  getProductInventoryStatus,
} from '@/lib/shopify';
import LookInsideModal from '../LookInsideModal/LookInsideModal';
import ProductVariantModal from './ProductVariantModal';
import PlaythingsModal from './PlaythingsModal';
import { SelectedAddOns } from '../ProductAddToCart/ProductAddToCart';
import useInventoryStatus from '@/hooks/useInventoryStatus';
import { InventoryStatusModal } from '../InventoryStatus';
import { getDefaultVariant } from '@/utils/shopify';
import { formatMoney } from '@/utils/helpers';
import useLocale from '@/hooks/useLocale';
import { announceChangeForScreenReader } from './utils';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  ITEM_EVENT_TYPES,
  track,
} from '@/utils/rudderstack';
import { usePathname } from 'next/navigation';
import { getFullUrl } from '@/utils/subflowAnalyticsHelpers';

interface ProductCtaButtonProps {
  isSelected: boolean;
  shopifyProduct: ShopifyProduct | null;
  handleClick: () => void;
  disableButtonIfOOS?: boolean;
}

const ProductCtaButton = ({
  isSelected,
  shopifyProduct,
  handleClick,
  disableButtonIfOOS = false,
}: ProductCtaButtonProps) => {
  const [isInventoryStatusModalOpen, setIsInventoryStatusModalOpen] =
    useState(false);
  const inventoryStatus = getProductInventoryStatus(shopifyProduct);
  const isProductOutOfStock =
    inventoryStatus === InventoryStatusCode.OUT_OF_STOCK;
  const [ctaText, ctaClasses, isCtaDisabled, modalTitle, modalDescription] =
    useInventoryStatus(shopifyProduct, false, true);
  const productCtaText = isSelected
    ? '- Remove'
    : isProductOutOfStock
      ? ctaText
      : '+ Add';

  return (
    <>
      <Button
        ctaText={productCtaText}
        disabled={isCtaDisabled || (isProductOutOfStock && disableButtonIfOOS)}
        onClick={() => {
          isProductOutOfStock
            ? setIsInventoryStatusModalOpen(true)
            : handleClick();
        }}
        style={BUTTON_STYLES.secondary}
        classes={classNames(
          'max-h-[48px] ml-auto px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-deep',
          ctaClasses,
        )}
        testId="product-addon-cta-btn"
      />

      {isProductOutOfStock && (
        <InventoryStatusModal
          isOpen={isInventoryStatusModalOpen}
          handleClose={() => setIsInventoryStatusModalOpen(false)}
          ctaText={'keep shopping'}
          title={modalTitle}
          description={modalDescription}
          data-testid="product-addon-inventory-modal"
        />
      )}
    </>
  );
};

interface ProductAddOnProps {
  productAddOn: TypeProduct;
  shopifyProductAddOn: ShopifyProduct | null;
  selectedAddOns: SelectedAddOns[];
  setSelectedAddOns: (selectedAddOns: SelectedAddOns[]) => void;
  isItemClickable?: boolean;
  isPlaythingProduct?: boolean;
}

const ProductAddOn: FC<ProductAddOnProps> = ({
  productAddOn,
  shopifyProductAddOn,
  selectedAddOns,
  setSelectedAddOns,
  isItemClickable = true,
  isPlaythingProduct = false,
}) => {
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const path = usePathname() || '';
  const [isProductDetailsModalOpen, setIsProductDetailsModalOpen] =
    useState(false);
  const isOrganicCottonBabyDoll =
    productAddOn.fields.shopifyProduct?.title === 'Organic Cotton Baby Doll' ||
    productAddOn.fields.shopifyProduct?.handle === 'thinker-doll';

  const [isSelected, setIsSelected] = useState(
    selectedAddOns.some(
      (addOn) => addOn.fields.entryName === productAddOn.fields.entryName,
    ),
  );
  const locale = useLocale();
  const {
    fields: { title, featuredImage },
  } = productAddOn;
  const defaultVariant = getDefaultVariant(
    productAddOn.fields.title,
    (shopifyProductAddOn as ShopifyProduct).variants,
  );
  const { formattedPrice, priceCentsStandalone } = defaultVariant;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);

  const addProduct = () => {
    setIsSelected(true);
    const addOnWithFieldsAndVariant = {
      fields: productAddOn.fields,
      variant: selectedVariant,
    };
    setSelectedAddOns([...selectedAddOns, addOnWithFieldsAndVariant]);
    announceChangeForScreenReader('added', title);
    track(ITEM_EVENT_TYPES.ITEM_CLICKED, {
      type: CLICKED_ITEM_TYPES.CTA,
      cta: CTA_NAMES.ADD_BUTTON,
      pageLocation: 'Individual Play Kit items',
      productName: title,
      productType: shopifyProductAddOn?.productType,
      url: getFullUrl(path),
    });
  };

  const removeProduct = () => {
    setIsSelected(false);
    setSelectedAddOns(
      selectedAddOns.filter(
        (addOn) => addOn.fields.entryName !== productAddOn.fields.entryName,
      ),
    );
    announceChangeForScreenReader('removed', title);
    track(ITEM_EVENT_TYPES.ITEM_CLICKED, {
      type: CLICKED_ITEM_TYPES.CTA,
      cta: CTA_NAMES.REMOVE_BUTTON,
      pageLocation: 'Individual Play Kit items',
      productName: title,
      productType: shopifyProductAddOn?.productType,
      url: getFullUrl(path),
    });
  };

  const addProductVariant = () => {
    setIsSelected(true);
    setIsVariantModalOpen(false);
    const addOnWithFieldsAndVariant = {
      fields: productAddOn.fields,
      variant: selectedVariant,
    };
    setSelectedAddOns([...selectedAddOns, addOnWithFieldsAndVariant]);
    announceChangeForScreenReader('added', selectedVariant.title);
  };

  const handleOrganicCottonBabyDollClick = () =>
    isOrganicCottonBabyDoll ? setIsVariantModalOpen(true) : addProduct();

  const handleClick = () =>
    isSelected ? removeProduct() : handleOrganicCottonBabyDollClick();

  return (
    <li
      className="mb-4 flex items-center justify-between"
      data-testid="product-addon-root"
    >
      <div className="flex items-center flex-1">
        <div
          className={classNames(
            'align-center flex h-[86px] w-[86px] flex-shrink-0 border-[3px] border-solid bg-khaki-tint',
            isSelected ? 'border-blue-deep' : 'border-transparent',
          )}
          data-testid="product-addon-image-container"
        >
          <LuvImage
            imageData={featuredImage}
            height={80}
            width={80}
            alt={featuredImage.fields.altText}
            className="object-contain"
          />
        </div>
        <div className="flex flex-col text-base">
          {isItemClickable ? (
            <Button
              ctaText={title}
              onClick={() => {
                setIsProductDetailsModalOpen(true);
              }}
              size={BUTTON_SIZES.md}
              style={BUTTON_STYLES.link}
              classes="text-left text-base h-auto min-h-fit px-3"
              testId="product-addon-title-btn"
            />
          ) : (
            <span
              className="text-left font-bold h-auto min-h-fit px-3"
              data-testid="product-addon-title-span"
            >
              {title}
            </span>
          )}
          <span className="px-3" data-testid="product-addon-price">
            {priceCentsStandalone
              ? formatMoney(priceCentsStandalone / 100, locale, false)
              : formattedPrice}
          </span>
        </div>
      </div>

      <ProductCtaButton
        isSelected={isSelected}
        shopifyProduct={shopifyProductAddOn}
        handleClick={handleClick}
        disableButtonIfOOS={
          shopifyProductAddOn?.productType === ShopifyProductType.KIT_COMPONENT
        }
      />

      <LookInsideModal
        isOpen={isProductDetailsModalOpen}
        handleClose={() => setIsProductDetailsModalOpen(false)}
        product={productAddOn}
        addProduct={addProduct}
        removeProduct={removeProduct}
        isSelected={isSelected}
        shopifyProduct={shopifyProductAddOn}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        data-testid="product-addon-lookinside-modal"
      />

      {isOrganicCottonBabyDoll &&
        (isPlaythingProduct ? (
          <PlaythingsModal
            isOpen={isVariantModalOpen}
            handleClose={() => setIsVariantModalOpen(false)}
            product={productAddOn}
            addProduct={addProductVariant}
            shopifyProductAddOn={shopifyProductAddOn}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            priceCentsStandalone={priceCentsStandalone}
            data-testid="product-addon-playthings-modal"
          />
        ) : (
          <ProductVariantModal
            isOpen={isVariantModalOpen}
            handleClose={() => setIsVariantModalOpen(false)}
            product={productAddOn}
            addProduct={addProductVariant}
            shopifyProductAddOn={shopifyProductAddOn}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            data-testid="product-addon-variant-modal"
          />
        ))}
    </li>
  );
};

export default React.memo(ProductAddOn);
